/**
 * AnalysisPresentation — renders AI-generated post-simulation analysis as navigable slides.
 *
 * Route: /analysis/:simRunId
 * Triggers LLM analysis call, then renders 12 slides with visualizations.
 *
 * SPEC: MODULES/M10_INFRASTRUCTURE/SPEC_DATA_EXPORT_ANALYSIS.md
 */

import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'

interface DataPoint { label: string; value: string }
interface Slide {
  slide: number; title: string; subtitle?: string; content: string
  data_points: DataPoint[]
  visualization_type: string
  visualization_data: Record<string, unknown>
}

const MODELS = [
  { id: 'claude-opus-4-7', label: 'Claude Opus 4.7 (best, 1M context)' },
  { id: 'claude-opus-4-6', label: 'Claude Opus 4.6 (1M context)' },
  { id: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (faster, 200K)' },
]

export function AnalysisPresentation() {
  const { simRunId } = useParams<{ simRunId: string }>()
  const [slides, setSlides] = useState<Slide[]>(() => {
    // Check if slides were pre-generated by SimAnalysisPage
    const stored = sessionStorage.getItem(`analysis_${simRunId}`)
    if (stored) {
      try { const d = JSON.parse(stored); return d.slides || [] } catch { /* */ }
    }
    return []
  })
  const [current, setCurrent] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [model, setModel] = useState('claude-opus-4-7')
  const [meta, setMeta] = useState<{ input_tokens?: number; output_tokens?: number; cost_usd?: number } | null>(() => {
    const stored = sessionStorage.getItem(`analysis_${simRunId}`)
    if (stored) {
      try { const d = JSON.parse(stored); return { input_tokens: d.input_tokens, output_tokens: d.output_tokens, cost_usd: d.cost_usd } } catch { /* */ }
    }
    return null
  })

  const generate = async () => {
    setLoading(true); setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) { setError('Not authenticated'); return }
      const resp = await fetch(`/api/sim/${simRunId}/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
        body: JSON.stringify({ model }),
      })
      if (!resp.ok) { const j = await resp.json().catch(() => ({})); setError(j.detail || `Error ${resp.status}`); return }
      const json = await resp.json()
      setSlides(json.data.slides || [])
      setMeta({ input_tokens: json.data.input_tokens, output_tokens: json.data.output_tokens, cost_usd: json.data.cost_usd })
      setCurrent(0)
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed') }
    finally { setLoading(false) }
  }

  // Arrow key navigation
  const handleKey = useCallback((e: KeyboardEvent) => {
    if (e.key === 'ArrowRight' || e.key === ' ') setCurrent(c => Math.min(c + 1, slides.length - 1))
    if (e.key === 'ArrowLeft') setCurrent(c => Math.max(c - 1, 0))
  }, [slides.length])

  useEffect(() => {
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [handleKey])

  // Pre-generation screen
  if (slides.length === 0) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <div className="max-w-md w-full p-8 text-center">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '24pt', color: '#1a1a2e', marginBottom: 12 }}>
            Post-Simulation Analysis
          </h1>
          <p style={{ color: '#666', fontSize: '11pt', marginBottom: 24 }}>
            Generate a comprehensive AI-driven debrief of the simulation — 12 slides covering outcomes, turning points, alliance dynamics, and role performance.
          </p>

          <div style={{ marginBottom: 16 }}>
            <label style={{ fontSize: '10pt', color: '#888', display: 'block', marginBottom: 4, textAlign: 'left' }}>Model</label>
            <select value={model} onChange={e => setModel(e.target.value)}
              style={{ width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: '11pt', background: '#fff' }}>
              {MODELS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </div>

          <button onClick={generate} disabled={loading}
            style={{ width: '100%', padding: '14px 0', background: loading ? '#999' : '#3B82F6', color: '#fff', border: 'none', borderRadius: 8, fontSize: '13pt', fontWeight: 600, cursor: loading ? 'wait' : 'pointer' }}>
            {loading ? 'Generating analysis... (this may take 1-2 minutes)' : 'Generate Analysis'}
          </button>

          {error && <p style={{ color: '#c0392b', marginTop: 12, fontSize: '10pt' }}>{error}</p>}
        </div>
      </div>
    )
  }

  // Slide viewer
  const slide = slides[current]

  return (
    <div className="min-h-screen bg-[#FAFAF8]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Navigation bar */}
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, background: '#fff', borderBottom: '1px solid #eee', padding: '8px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: '13pt', color: '#1a1a2e' }}>Thucydides Trap — Analysis</span>
          <span style={{ fontSize: '10pt', color: '#888' }}>Slide {current + 1} / {slides.length}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => setCurrent(c => Math.max(c - 1, 0))} disabled={current === 0}
            style={{ padding: '4px 12px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', opacity: current === 0 ? 0.3 : 1 }}>←</button>
          <button onClick={() => setCurrent(c => Math.min(c + 1, slides.length - 1))} disabled={current === slides.length - 1}
            style={{ padding: '4px 12px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', opacity: current === slides.length - 1 ? 0.3 : 1 }}>→</button>
          <button onClick={() => {
            const html = document.documentElement.outerHTML
            const blob = new Blob([html], { type: 'text/html' })
            const url = URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url; a.download = `TTT_Analysis_${new Date().toISOString().slice(0, 10)}.html`
            a.click(); URL.revokeObjectURL(url)
          }} style={{ padding: '4px 12px', border: '1px solid #ddd', borderRadius: 4, background: '#fff', cursor: 'pointer', fontSize: '10pt' }}>
            Download HTML
          </button>
          {meta && <span style={{ fontSize: '9pt', color: '#aaa' }}>${meta.cost_usd?.toFixed(2)} · {meta.input_tokens?.toLocaleString()}→{meta.output_tokens?.toLocaleString()} tokens</span>}
        </div>
      </div>

      {/* Slide content */}
      <div style={{ paddingTop: 56, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        onClick={() => setCurrent(c => Math.min(c + 1, slides.length - 1))}>
        <div style={{ maxWidth: 900, width: '100%', padding: '40px 48px', background: '#fff', borderRadius: 12, boxShadow: '0 2px 20px rgba(0,0,0,0.06)', margin: '24px auto', minHeight: 500 }}>

          {/* Title */}
          <div style={{ marginBottom: 24 }}>
            <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: '22pt', color: '#1a1a2e', margin: 0 }}>
              {slide.title}
            </h1>
            {slide.subtitle && <p style={{ fontSize: '12pt', color: '#666', marginTop: 4 }}>{slide.subtitle}</p>}
          </div>

          {/* Data points row */}
          {slide.data_points?.length > 0 && (
            <div style={{ display: 'flex', gap: 24, marginBottom: 20, flexWrap: 'wrap' }}>
              {slide.data_points.map((dp, i) => (
                <div key={i}>
                  <div style={{ fontSize: '9pt', color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em' }}>{dp.label}</div>
                  <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '16pt', color: '#1a1a2e', fontWeight: 700 }}>{dp.value}</div>
                </div>
              ))}
            </div>
          )}

          {/* Content */}
          <div style={{ fontSize: '11pt', lineHeight: 1.6, color: '#333', whiteSpace: 'pre-wrap' }}>
            {slide.content}
          </div>

          {/* Visualization */}
          {slide.visualization_type && slide.visualization_type !== 'none' && slide.visualization_data && (
            <div style={{ marginTop: 24 }}>
              <SlideVisualization type={slide.visualization_type} data={slide.visualization_data} />
            </div>
          )}
        </div>
      </div>

      {/* Slide dots */}
      <div style={{ position: 'fixed', bottom: 16, left: 0, right: 0, display: 'flex', justifyContent: 'center', gap: 6 }}>
        {slides.map((_, i) => (
          <button key={i} onClick={(e) => { e.stopPropagation(); setCurrent(i) }}
            style={{ width: i === current ? 20 : 8, height: 8, borderRadius: 4, border: 'none', background: i === current ? '#3B82F6' : '#ddd', cursor: 'pointer', transition: 'all 0.2s' }} />
        ))}
      </div>
    </div>
  )
}

/** Renders visualization based on type + data from LLM response */
function SlideVisualization({ type, data }: { type: string; data: Record<string, unknown> }) {
  if (type === 'scenario_cards') {
    const scenarios = (data.scenarios as { name: string; probability: number; description: string }[]) || []
    return (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.min(scenarios.length, 4)}, 1fr)`, gap: 12 }}>
        {scenarios.map((s, i) => (
          <div key={i} style={{ border: '1px solid #ddd', borderRadius: 8, padding: 16, background: '#FAFAF8' }}>
            <div style={{ fontSize: '9pt', color: '#888', marginBottom: 4 }}>Scenario {i + 1}</div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '18pt', color: '#3B82F6', fontWeight: 700 }}>{s.probability}%</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: '11pt', color: '#1a1a2e', margin: '4px 0', fontWeight: 600 }}>{s.name}</div>
            <div style={{ fontSize: '9.5pt', color: '#555', lineHeight: 1.4 }}>{s.description}</div>
          </div>
        ))}
      </div>
    )
  }

  if (type === 'checklist') {
    const items = (data.items as { label: string; checked: boolean; evidence: string }[]) || []
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((item, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '8px 12px', background: item.checked ? '#f0fdf4' : '#fef2f2', borderRadius: 6, border: `1px solid ${item.checked ? '#bbf7d0' : '#fecaca'}` }}>
            <span style={{ fontSize: '16pt', flexShrink: 0 }}>{item.checked ? '✓' : '✗'}</span>
            <div>
              <div style={{ fontWeight: 600, fontSize: '10pt', color: '#1a1a2e' }}>{item.label}</div>
              <div style={{ fontSize: '9pt', color: '#666' }}>{item.evidence}</div>
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (type === 'scores') {
    const roles = (data.roles as { name: string; country: string; score: number; note: string }[]) || []
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {roles.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
            <span style={{ fontWeight: 600, fontSize: '10pt', width: 140, flexShrink: 0 }}>{r.name}</span>
            <span style={{ fontSize: '9pt', color: '#888', width: 80, flexShrink: 0 }}>{r.country}</span>
            <div style={{ flex: 1, height: 14, background: '#eee', borderRadius: 7, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${r.score * 10}%`, background: r.score >= 7 ? '#22c55e' : r.score >= 4 ? '#f59e0b' : '#ef4444', borderRadius: 7 }} />
            </div>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '11pt', fontWeight: 700, width: 30 }}>{r.score}</span>
            <span style={{ fontSize: '9pt', color: '#666', flex: 2 }}>{r.note}</span>
          </div>
        ))}
      </div>
    )
  }

  if (type === 'ranking') {
    const rows = (data.rows as { name: string; score?: number; trend: string; note: string }[]) || []
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '3px 0', borderBottom: '1px solid #f0f0f0' }}>
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: '10pt', color: '#888', width: 24 }}>#{i + 1}</span>
            <span style={{ fontWeight: 600, fontSize: '10pt', width: 100 }}>{r.name}</span>
            <span style={{ fontSize: '12pt' }}>{r.trend === 'up' ? '▲' : r.trend === 'down' ? '▼' : '—'}</span>
            <span style={{ fontSize: '9pt', color: '#666', flex: 1 }}>{r.note}</span>
          </div>
        ))}
      </div>
    )
  }

  if (type === 'timeline') {
    const events = (data.events as { round: number; label: string; type: string }[]) || []
    return (
      <div style={{ position: 'relative', padding: '12px 0' }}>
        <div style={{ position: 'absolute', top: '50%', left: 0, right: 0, height: 2, background: '#ddd' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', position: 'relative' }}>
          {events.map((ev, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: 120 }}>
              <div style={{ width: 12, height: 12, borderRadius: '50%', background: ev.type === 'turning_point' ? '#ef4444' : '#3B82F6', border: '2px solid #fff', boxShadow: '0 0 0 2px #ddd', zIndex: 1 }} />
              <div style={{ fontSize: '8pt', color: '#888', marginTop: 4 }}>R{ev.round}</div>
              <div style={{ fontSize: '9pt', color: '#333', textAlign: 'center', marginTop: 2 }}>{ev.label}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (type === 'comparison') {
    const tracks = (data.tracks as { name: string; values: number[] }[]) || []
    if (tracks.length === 0) return null
    const maxVal = Math.max(...tracks.flatMap(t => t.values))
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {tracks.map((t, i) => (
          <div key={i}>
            <div style={{ fontSize: '9pt', fontWeight: 600, color: '#1a1a2e', marginBottom: 4 }}>{t.name}</div>
            <div style={{ display: 'flex', gap: 2, alignItems: 'flex-end', height: 40 }}>
              {t.values.map((v, j) => (
                <div key={j} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{ width: '80%', background: i === 0 ? '#3B82F6' : '#f59e0b', borderRadius: 2, height: `${(v / maxVal) * 36}px`, minHeight: 2 }} />
                  <span style={{ fontSize: '7pt', color: '#aaa', marginTop: 2 }}>R{j + 1}</span>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    )
  }

  return null
}
